import React, { ReactNode, useState } from "react";
import PropType from "prop-types";
import { useTranslate } from "../../../language/i18n";
import { Button, THEME } from "@myloc/myloc-gui";
import Modal from "./Modal";
import styles from "./LogisticsConfirmModal.module.scss";
import { myloc } from "../../../config/settings";
import { ExternalLink, TYPE } from "../Link";

function LogisticsConfirmModal({
  title,
  text,
  isHtmlContent,
  visible,
  cancel,
  submit,
  children,
  small = true,
  customCssClass,
  showButtons = true,
  showCloseButton,
  showConfirmButton = true,
  showDeclineButton = true,
  confirmButtonText,
  confirmButtonDisabled,
  declineButtonText,
  showSupportEmail = false,
}: {
  title: string;
  text: string;
  isHtmlContent?: boolean;
  visible: boolean;
  cancel: () => void;
  submit?: () => void;
  children?: ReactNode;
  small?: boolean;
  customCssClass?: string;
  showButtons?: boolean;
  showCloseButton?: boolean;
  showConfirmButton?: boolean;
  showDeclineButton?: boolean;
  confirmButtonText?: string;
  confirmButtonDisabled?: boolean;
  declineButtonText?: string;
  showSupportEmail?: boolean;
}) {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Modal
      title={translate(title)}
      visible={visible}
      onClose={cancel}
      small={small}
      customCssClass={customCssClass}
      showCloseButton={showCloseButton}
    >
      {isHtmlContent ? (
        <div dangerouslySetInnerHTML={{ __html: text }} />
      ) : (
        <p>
          {translate(text)}
          {showSupportEmail && (
            <ExternalLink url={myloc.supportEmail} type={TYPE.EMAIL}>
              {myloc.supportEmail.toString}
            </ExternalLink>
          )}
        </p>
      )}
      {children}
      {showButtons && (
        <footer className={styles.buttons}>
          {showConfirmButton && (
            <Button
              isLoading={isLoading}
              disabled={confirmButtonDisabled}
              onClick={() => {
                setIsLoading(true);

                if (submit) submit();

                setIsLoading(false);
                cancel();
              }}
            >
              {translate(confirmButtonText ?? "YES")}
            </Button>
          )}

          {showDeclineButton && (
            <Button theme={THEME.SECONDARY} onClick={cancel}>
              {translate(declineButtonText ?? "NO")}
            </Button>
          )}
        </footer>
      )}
    </Modal>
  );
}

LogisticsConfirmModal.propTypes = {
  title: PropType.string.isRequired,
  text: PropType.string.isRequired,
  isHtmlContent: PropType.bool,
  visible: PropType.bool.isRequired,
  cancel: PropType.func.isRequired,
  submit: PropType.func,
  children: PropType.node,
  small: PropType.bool,
  customCssClass: PropType.string,
  showButtons: PropType.bool,
  showCloseButton: PropType.bool,
  showConfirmButton: PropType.bool,
  showDeclineButton: PropType.bool,
  confirmButtonText: PropType.string,
  confirmButtonDisabled: PropType.bool,
  declineButtonText: PropType.string,
  showSupportEmail: PropType.bool,
};

export default LogisticsConfirmModal;
